import { SimpleSidebar } from "@volca/ui/components/ui/simple-sidebar";
import { cn } from "@volca/ui/lib/utils";
import { IcmJobSidebar } from "@/icm/common";
import { AiScriptPersistentEditsSidebar } from "@/payroll/ai-script/ai-script-persistent-edits-panel";
import { CellCommentsSidebar } from "@/payroll/ai-script/cell-comments/cell-comments-sidebar";
import { PacksSidebar } from "@/payroll/pay-breakdown/payroll-statement-notifications/packs-section";
import {
  ST_ADJUSTMENTS_ACCESS,
  StAdjustmentsSidebar,
} from "@/payroll/pay-breakdown/st-adjustments";
import {
  PAY_BREAKDOWN_RUN_TARGET_TYPE,
  PAY_BREAKDOWN_SECTION,
} from "@/payroll/pay-breakdown/types";
import { PayrollPeriodNotes } from "@/payroll/period-notes";
import type { PayBreakdownPageState } from "./use-pay-breakdown-page";

interface PayBreakdownSidebarProps {
  page: PayBreakdownPageState;
}

export const PayBreakdownSidebar = ({ page }: PayBreakdownSidebarProps) => {
  const {
    businessId,
    selectedPeriodStart,
    isLocked,
    output,
    hasActiveRun,
    stAdjustmentsAccess,
    activeSection,
    isCreatingAiRun,
    isTriggeringRun,
    selectedJob,
    setSelectedJob,
    setSectionSidebarOpen,
    isCommentsOpen,
    setIsCommentsOpen,
    isManualEditsOpen,
    focusedEdit,
    closeManualEdits,
    showAllManualEdits,
    focusManualEdit,
    isSheetMaximized,
    spreadsheetViewerRef,
    handleGoToCell,
    registerSectionSidebarClose,
    canUseNotes,
    isNotesOpen,
    setIsNotesOpen,
  } = page;

  return (
    <SimpleSidebar
      className={cn({
        "fixed top-0 z-40 h-screen": isSheetMaximized,
      })}
    >
      {canUseNotes && selectedPeriodStart && (
        <PayrollPeriodNotes
          key={`${businessId}:${selectedPeriodStart}`}
          businessId={businessId}
          periodStart={selectedPeriodStart}
          locked={isLocked}
          open={isNotesOpen}
          onClose={() => setIsNotesOpen(false)}
        />
      )}
      {activeSection === PAY_BREAKDOWN_SECTION.packs && (
        <PacksSidebar
          onOpenChange={setSectionSidebarOpen}
          registerClose={registerSectionSidebarClose}
        />
      )}
      {activeSection === PAY_BREAKDOWN_SECTION.adjustments &&
        stAdjustmentsAccess !== ST_ADJUSTMENTS_ACCESS.hidden && (
          <StAdjustmentsSidebar
            onOpenChange={setSectionSidebarOpen}
            registerClose={registerSectionSidebarClose}
          />
        )}
      {selectedJob && (
        <IcmJobSidebar
          job={selectedJob}
          periodStart={selectedPeriodStart}
          onClose={() => setSelectedJob(null)}
        />
      )}
      {isCommentsOpen &&
        output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript && (
          <CellCommentsSidebar
            key={output.runId}
            businessId={businessId}
            runId={output.runId}
            onGoToThread={({ placement }) => {
              if (
                placement.sheet_id &&
                placement.row != null &&
                placement.col != null
              ) {
                spreadsheetViewerRef.current?.openCommentsAt(
                  placement.sheet_id,
                  placement.row,
                  placement.col,
                );
              }
            }}
            onClose={() => setIsCommentsOpen(false)}
          />
        )}
      {isManualEditsOpen &&
        output.type === PAY_BREAKDOWN_RUN_TARGET_TYPE.aiScript && (
          <AiScriptPersistentEditsSidebar
            key={output.runId}
            businessId={businessId}
            runId={output.runId}
            isLocked={isLocked}
            isRunning={hasActiveRun || isCreatingAiRun || isTriggeringRun}
            focusEdit={focusedEdit}
            onShowAll={showAllManualEdits}
            onFocusEdit={focusManualEdit}
            onGoToCell={handleGoToCell}
            onClose={closeManualEdits}
            onWorkbookMutated={() =>
              spreadsheetViewerRef.current?.resetHistory()
            }
          />
        )}
    </SimpleSidebar>
  );
};
